import React from 'react';

import { requestMembership } from '@lumapps/communities/api';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL } from '@lumapps/translations';

import { selectSpaceId } from '../ducks/selectors';
import { useInvalidateSpacesQueries } from './useInvalidateSpacesQueries';

export interface UseSpaceAccessRequestResult {
    /** Whether the request is in progress (held until the space data is refetched). */
    isLoading: boolean;
    /** Triggers the membership request. */
    requestAccess: () => Promise<void>;
}

/**
 * Hook to request access (membership) to the current space.
 *
 * Calls the membership request API and invalidates the space queries so that
 * the refetched space data reflects the pending request status.
 */
export const useSpaceAccessRequest = (): UseSpaceAccessRequestResult => {
    const spaceId = useSelector(selectSpaceId);
    const invalidateSpacesQueries = useInvalidateSpacesQueries();
    const { error: notifyError } = useNotification();
    const [isLoading, setIsLoading] = React.useState(false);

    const requestAccess = React.useCallback(async () => {
        if (!spaceId) {
            return;
        }

        setIsLoading(true);

        try {
            await requestMembership({ communityKey: spaceId });
        } catch (exception) {
            notifyError({ translate: GLOBAL.GENERIC_ERROR });
            setIsLoading(false);
            return;
        }

        try {
            // Await so the button stays disabled until fresh myMembership data is refetched
            await invalidateSpacesQueries();
        } catch (exception) {
            // The membership request itself succeeded; a failed cache refresh only leaves
            // stale UI, so don't show an error suggesting the request failed.
        } finally {
            setIsLoading(false);
        }
    }, [spaceId, invalidateSpacesQueries, notifyError]);

    return { isLoading, requestAccess };
};
